<template>
  <div class="chat-container">
    <Row class="chat-row">
      <Col span="6" class="left-panel">
        <Card class="online-card">
          <div class="online-title">
            <Icon type="ios-people" size="18" />
            在线修士
            <span class="online-count">{{filteredUsers.length}}</span>
          </div>

          <div class="channel-list">
            <!-- 系统消息频道 -->
            <div
              class="channel-item"
              :class="{ active: activeChannel.id === 'system' }"
              @click="switchChannel({ id: 'system', name: '系统消息', type: 'system' })">
              <div class="channel-info">
                <Icon type="ios-notifications-outline" size="16" />
                <span class="channel-name">系统消息</span>
              </div>
              <Badge :count="getUnreadCount('system')" class="channel-badge"></Badge>
            </div>

            <!-- 世界频道 -->
            <div
              class="channel-item"
              :class="{ active: activeChannel.id === 'world' }"
              @click="switchChannel({ id: 'world', name: '世界频道', type: 'world' })">
              <div class="channel-info">
                <Icon type="ios-globe" size="16" />
                <span class="channel-name">世界频道</span>
              </div>
              <Badge :count="getUnreadCount('world')" class="channel-badge"></Badge>
            </div>

            <!-- 桑林村村口 -->
            <div
              class="channel-item"
              :class="{ active: activeChannel.id === 'village' }"
              @click="switchChannel({ id: 'village', name: '桑林村村口', type: 'channel' })">
              <div class="channel-info">
                <Icon type="ios-home" size="16" />
                <span class="channel-name">桑林村村口</span>
              </div>
              <Badge :count="getUnreadCount('village')" class="channel-badge"></Badge>
            </div>

            <!-- 分隔线 -->
            <div class="channel-divider">
              <Icon type="ios-contact" size="14" />
              在线修士 ({{filteredUsers.length}})
            </div>

            <!-- 在线用户列表 -->
            <div
              v-for="user in filteredUsers"
              :key="user.account"
              class="user-item"
              :class="{ active: activeChannel.id === user.account }"
              @click="switchToPrivateChat(user)">
<!--              <Avatar
                size="small"
                :src="getUserFace(user.face)"
                class="user-avatar"
              />-->
              <div class="user-info">
                <div class="user-name">{{ user.userName }}</div>
                <div class="user-details">
                  <Tag color="primary" size="small">Lv.{{ user.level }}</Tag>
                  <Tag color="success" size="small">{{ user.realmName }}</Tag>
                </div>
              </div>
              <Badge :count="user.unread || 0" class="user-badge"></Badge>
            </div>

            <div v-if="filteredUsers.length === 0" class="empty-users">
              暂无其他在线修士
            </div>
          </div>
        </Card>
      </Col>

      <Col span="18" class="right-panel">
        <div class="chat-main">
          <!-- 聊天头部 -->
          <div class="chat-header">
            <div class="chat-title">
              <Icon type="ios-book" size="20" />
              <span class="title-text">书信阁</span>
              <span class="channel-name">· {{ activeChannel.name }}</span>
            </div>
            <div class="header-actions">
              <Tooltip content="消息中心" placement="bottom">
                <Badge
                  :count="totalUnread"
                  class="total-badge"
                  @click.native="showMessageCenter">
                  <Icon type="ios-chatbubbles" size="22" />
                </Badge>
              </Tooltip>
            </div>
          </div>

          <!-- 消息区域 -->
          <div
            ref="messageContainer"
            class="message-container">

            <!-- 欢迎提示 -->
            <div class="welcome-message">
              <div class="welcome-content">
                <Icon type="ios-flower" size="24" color="#6e44c0" />
                <h3>欢迎来到书信阁</h3>
                <p>与各路修士交流心得，共探大道</p>
              </div>
            </div>

            <!-- 系统消息 -->
            <div
              v-for="msg in recentSystemMessages"
              :key="msg.id"
              class="system-message">
              <div class="system-content">
                <Icon type="ios-information-circle-outline" size="14" />
                {{ msg.content }}
                <span class="system-time">{{ formatTime(msg.timestamp) }}</span>
              </div>
            </div>

            <!-- 聊天消息 -->
            <div
              v-for="(msg, index) in currentMessages"
              :key="index"
              class="message-wrapper"
              :class="msg.from === user.account ? 'self' : 'other'">

              <!-- 世界消息 -->
              <div v-if="msg.type === 'world'" class="world-message">
                <Avatar
                  size="small"
                  :src="getUserAvatar(msg.from)"
                  class="world-avatar"
                />
                <div class="world-content">
                  <div class="world-header">
                    <span class="world-tag">🌍 [世界]</span>
                    <strong class="world-sender">{{ getDisplayName(msg.from) }}</strong>
                    <div class="world-user-info">
                      <Tag color="primary" size="mini">Lv.{{ getUserLevel(msg.from) }}</Tag>
                      <Tag color="success" size="mini">{{ getUserRealm(msg.from) }}</Tag>
                    </div>
                  </div>
                  <div class="world-text">{{ msg.text }}</div>
                  <div class="message-time">{{ formatTime(msg.timestamp) }}</div>
                </div>
              </div>

              <!-- 普通消息 -->
              <template v-else>
                <!-- 其他人消息 -->
                <div v-if="msg.from !== user.account" class="other-message">
                  <div class="message-avatar-container">
                    <Avatar
                      size="default"
                      :src="msg.fromFace"
                      class="message-avatar"
                    />
                    <div class="avatar-badge">Lv.{{ msg.fromLevel }}</div>
                  </div>
                  <div class="message-content">
                    <div class="message-info">
                      <span class="sender-name">{{ msg.fromNickname }}</span>
                      <Tag color="success" size="mini">{{ msg.fromRealm }}</Tag>
                      <span class="message-time">{{ formatTime(msg.timestamp) }}</span>
                    </div>
                    <div class="bubble other-bubble">
                      {{ msg.text }}
                    </div>
                  </div>
                </div>

                <!-- 自己消息 -->
                <div v-else class="self-message">
                  <div class="message-content">
                    <div class="message-info self-info">
                      <span class="message-time">{{ formatTime(msg.timestamp) }}</span>
                      <Tag color="success" size="mini">{{ user.realmName }}</Tag>
                      <span class="sender-name">{{ user.userName || '我' }}</span>
                    </div>
                    <div class="bubble self-bubble">
                      {{ msg.text }}
                    </div>
                  </div>
                  <div class="message-avatar-container">
                    <Avatar
                      size="default"
                      :src="getUserFace(user.face)"
                      class="message-avatar"
                    />
                    <div class="avatar-badge self-badge">Lv.{{ user.level }}</div>
                  </div>
                </div>
              </template>
            </div>
          </div>

          <!-- 输入区域 -->
          <div class="input-area">
            <div class="input-toolbar">
              <div class="toolbar-left">
                <RadioGroup v-model="sendType" size="small">
                  <Radio label="normal">
                    <Icon type="ios-chatbubbles" size="14" />
                    <span>普通</span>
                  </Radio>
                  <Radio label="world">
                    <Icon type="ios-globe" size="14" />
                    <span>世界</span>
                  </Radio>
                </RadioGroup>
              </div>
              <div class="toolbar-right">
                <span class="user-info-tag">
                  <Avatar
                    size="small"
                    :src="getUserFace(user.face)"
                    style="margin-right: 4px;"
                  />
                  {{ user.userName }} · Lv.{{ user.level }} · {{ user.realmName }}
                </span>
              </div>
            </div>

            <div class="textarea-container">
              <textarea
                v-model="text"
                class="chat-textarea"
                :placeholder="sendType === 'world' ? '输入世界消息... (Ctrl+Enter发送)' : '输入消息... (Ctrl+Enter发送)'"
                @keydown.ctrl.enter="send"
                rows="4"
              ></textarea>
              <div class="textarea-tips">
                <Icon type="ios-information-circle-outline" size="12" />
                支持表情、文字，请文明交流
              </div>
            </div>

            <div class="input-actions">
              <div class="actions-left">
                <Button type="text" size="small" @click="clearChat">
                  <Icon type="ios-trash" />
                  清空当前
                </Button>
              </div>
              <div class="actions-right">
                <Button type="warning" size="small" @click="text = ''">
                  <Icon type="ios-backspace" />
                  清空输入
                </Button>
                <Button type="primary" size="small" @click="send">
                  <Icon type="ios-send" />
                  发送消息
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Col>
    </Row>

    <!-- 消息中心弹窗 -->
    <Modal
      v-model="messageModalVisible"
      title="消息中心"
      width="800"
      class-name="message-center-modal"
      :mask-closable="false">
      <div class="modal-header">
        <Icon type="ios-stats" size="20" style="margin-right: 8px;" />
        <span>消息统计：系统消息({{ allSystemMessages.length }}) | 世界消息({{ worldMessages.length }}) | 私聊记录</span>
      </div>

      <Tabs v-model="activeTab" class="message-tabs">
        <TabPane label="📢 系统消息" name="system">
          <div class="message-list">
            <div
              v-for="msg in allSystemMessages"
              :key="msg.id"
              class="message-item">
              <div class="message-item-header">
                <Icon type="ios-information-circle" size="16" color="#2d8cf0" />
                <span class="message-item-time">{{ formatFullTime(msg.timestamp) }}</span>
              </div>
              <div class="message-item-content">{{ msg.content }}</div>
            </div>
            <div v-if="allSystemMessages.length === 0" class="empty-message">
              <Icon type="ios-information-circle-outline" size="48" />
              <p>暂无系统消息</p>
            </div>
          </div>
        </TabPane>

        <TabPane label="🌍 世界消息" name="world">
          <div class="message-list">
            <div
              v-for="msg in worldMessages"
              :key="msg.id"
              class="message-item">
              <div class="message-item-header">
                <Avatar size="small" :src="getUserAvatar(msg.from)" />
                <div class="message-item-sender-info">
                  <strong class="message-item-sender">{{ getDisplayName(msg.from) }}</strong>
                  <div class="sender-details">
                    <Tag color="primary" size="mini">Lv.{{ getUserLevel(msg.from) }}</Tag>
                    <Tag color="success" size="mini">{{ getUserRealm(msg.from) }}</Tag>
                  </div>
                </div>
                <span class="message-item-time">{{ formatFullTime(msg.timestamp) }}</span>
              </div>
              <div class="message-item-content">{{ msg.text }}</div>
            </div>
            <div v-if="worldMessages.length === 0" class="empty-message">
              <Icon type="ios-chatbubbles-outline" size="48" />
              <p>暂无世界消息</p>
            </div>
          </div>
        </TabPane>

        <TabPane label="💬 私聊记录" name="private">
          <div class="private-tab">
            <div class="user-selector">
              <Select
                v-model="selectedPrivateUser"
                placeholder="选择修士查看私聊记录"
                style="width: 100%;"
                clearable
                filterable>
                <Option
                  v-for="user in privateChatUsers"
                  :key="user.account"
                  :value="user.account"
                  :label="user.userName">
                  <div style="display: flex; align-items: center;">
                    <Avatar size="small" :src="getUserFace(user.face)" />
                    <div style="margin-left: 8px; line-height: 1.2;">
                      <div>{{ user.userName }}</div>
                      <div style="font-size: 11px; color: #999;">
                        Lv.{{ user.level }} · {{ user.realmName }}
                      </div>
                    </div>
                  </div>
                </Option>
              </Select>
            </div>

            <div class="message-list">
              <div
                v-for="msg in selectedPrivateMessages"
                :key="msg.id"
                class="message-item"
                :class="{ 'self-item': msg.from === user.account }">
                <div class="message-item-header">
                  <Avatar size="small" :src="getUserAvatar(msg.from)" />
                  <div class="message-item-sender-info">
                    <span class="message-item-sender">
                      {{ msg.from === user.account ? '我' : getDisplayName(msg.from) }}
                    </span>
                    <div class="sender-details">
                      <Tag color="primary" size="mini">
                        Lv.{{ msg.from === user.account ? user.level : getUserLevel(msg.from) }}
                      </Tag>
                    </div>
                  </div>
                  <span class="message-item-time">{{ formatFullTime(msg.timestamp) }}</span>
                </div>
                <div class="message-item-content">{{ msg.text }}</div>
              </div>
              <div v-if="selectedPrivateMessages.length === 0" class="empty-message">
                <Icon type="ios-person-outline" size="48" />
                <p>{{ selectedPrivateUser ? '暂无与该修士的私聊记录' : '选择修士查看私聊记录' }}</p>
              </div>
            </div>
          </div>
        </TabPane>
      </Tabs>
      <div slot="footer">
        <Button type="warning" @click="clearAllUnread">
          <Icon type="ios-checkmark-circle" />
          全部标记为已读
        </Button>
        <Button @click="messageModalVisible = false">
          <Icon type="ios-close" />
          关闭
        </Button>
      </div>
    </Modal>
  </div>
</template>

<script>
export default {
  name: "Im",
  data() {
    return {
      user: {
        account: '',      // 用户编号
        userName: '',     // 用户名称
        face: '',         // 头像
        level: 0,         // 等级
        realmName: ''     // 境界
      },
      users: [],          // 在线用户列表
      chatChannels: [
        {
          id: 'system',
          name: '系统消息',
          type: 'system',
          unread: 0,
          messages: [],
          icon: 'ios-notifications-outline'
        },
        {
          id: 'world',
          name: '世界频道',
          type: 'world',
          unread: 0,
          messages: [],
          icon: 'ios-globe'
        },
        {
          id: 'village',
          name: '桑林村村口',
          type: 'channel',
          unread: 0,
          messages: [],
          icon: 'ios-home'
        }
      ],
      activeChannel: {
        id: 'village',
        name: '桑林村村口',
        type: 'channel'
      },
      sendType: 'normal',
      text: "",

      // 消息存储
      systemMessages: [],
      worldMessages: [],
      userMessages: {},
      maxChannelMessages: 200,

      // 弹窗
      messageModalVisible: false,
      activeTab: 'system',
      selectedPrivateUser: '',

      // WebSocket相关
      /*socketObj: null,
      heartCheck: {
        vueThis: this,
        timeout: 10000,
        timeoutObj: null,
        serverTimeoutObj: null,
        reset: function () {
          clearTimeout(this.timeoutObj);
          clearTimeout(this.serverTimeoutObj);
          return this;
        },
        start: function () {
          this.timeoutObj && clearTimeout(this.timeoutObj);
          this.serverTimeoutObj && clearTimeout(this.serverTimeoutObj);

          this.timeoutObj = setTimeout(() => {
            this.vueThis.socketObj.send("HeartBeat");
            this.serverTimeoutObj = setTimeout(() => {
              this.vueThis.socketObj.close();
            }, this.timeout);
          }, this.timeout);
        },
      },
      socketReconnectTimer: null,
      socketReconnectLock: false,
      socketLeaveFlag: false,*/
    }
  },
  computed: {
    filteredUsers() {
      const currentAccount = this.user.account;
      return this.users.filter(user => user.account !== currentAccount)
        .map(user => ({
          ...user,
          unread: user.unread || 0,
          messages: user.messages || []
        }));
    },
    totalUnread() {
      let total = 0;
      // 频道未读
      this.chatChannels.forEach(channel => {
        if (channel.id !== this.activeChannel.id) {
          total += channel.unread || 0;
        }
      });
      // 用户未读
      this.filteredUsers.forEach(user => {
        if (user.account !== this.activeChannel.id) {
          total += user.unread || 0;
        }
      });
      return total;
    },
    currentMessages() {
      if (this.activeChannel.type === 'private') {
        return this.getPrivateMessages(this.activeChannel.id);
      } else {
        const channel = this.chatChannels.find(c => c.id === this.activeChannel.id);
        return channel ? channel.messages : [];
      }
    },
    recentSystemMessages() {
      return this.systemMessages.slice(-5);
    },
    allSystemMessages() {
      const systemChannel = this.chatChannels.find(c => c.id === 'system');
      return systemChannel ? systemChannel.messages : [];
    },
    privateChatUsers() {
      return this.filteredUsers.filter(user =>
        (user.messages && user.messages.length > 0) || user.unread > 0
      );
    },
    selectedPrivateMessages() {
      if (!this.selectedPrivateUser) return [];
      const user = this.users.find(u => u.account === this.selectedPrivateUser);
      return user ? user.messages || [] : [];
    },
  },

  created() {
    // 监听小窗口发送的消息
    this.$bus.$on('SEND_CHAT_MESSAGE', this.handleSendChatMessage);
  },
  beforeDestroy() {
    // 清理所有事件监听
    this.$bus.$off('SEND_CHAT_MESSAGE', this.handleSendChatMessage);
    this.$bus.$off('UPDATE_UNREAD_COUNT', this.updateUnreadCount);
    this.cleanupEventListeners(); // 调用清理全局 WebSocket 监听的方法
  },
  mounted() {
    this.initUserData();
    //this.createWebSocket();
    this.setupGlobalWebSocket();
  },
  destroyed() {
    // 清理所有事件监听
    this.$bus.$off('SEND_CHAT_MESSAGE', this.handleSendChatMessage);
    this.$bus.$off('UPDATE_UNREAD_COUNT', this.updateUnreadCount);
    this.cleanupEventListeners();
  },
  // 将 watch 移到正确的位置
  watch: {
    totalUnread(newVal) {
      // 当未读消息变化时，通知全局
      this.$bus.$emit('GLOBAL_UNREAD_UPDATE', newVal);
    },

    currentMessages() {
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },

    activeChannel(newChannel) {
      this.clearUnread(newChannel.id);
    }
  },
  methods: {
    // ========== 全局 WebSocket 集成 ==========
    setupGlobalWebSocket() {
      // 监听全局 WebSocket 消息
      this.$bus.$on('GLOBAL_WS_MESSAGE', this.handleGlobalWsMessage);

      // 监听全局 WebSocket 状态变化
      this.$bus.$on('GLOBAL_WS_OPEN', this.handleGlobalWsOpen);
      this.$bus.$on('GLOBAL_WS_CLOSE', this.handleGlobalWsClose);
      this.$bus.$on('GLOBAL_WS_ERROR', this.handleGlobalWsError);

      // 如果全局 WebSocket 已经连接，立即触发连接成功
      if (window.chatWebSocket && window.chatWebSocket.readyState === WebSocket.OPEN) {
        this.handleGlobalWsOpen();
      }
    },

    cleanupEventListeners() {
      this.$bus.$off('GLOBAL_WS_MESSAGE', this.handleGlobalWsMessage);
      this.$bus.$off('GLOBAL_WS_OPEN', this.handleGlobalWsOpen);
      this.$bus.$off('GLOBAL_WS_CLOSE', this.handleGlobalWsClose);
      this.$bus.$off('GLOBAL_WS_ERROR', this.handleGlobalWsError);
    },
    handleGlobalWsMessage(data) {
      // 这里处理从全局 WebSocket 收到的消息
      // 原有的 getMessageCallback 逻辑搬到这里
      console.log("收到全局 WebSocket 消息:", data);

      if (data === "HeartBeat") {
        // 心跳响应，不需要处理
        return;
      }

      if (data === "连接成功") {
        //console.log("全局 WebSocket 连接成功");
        return;
      }

      try {
        const parsedData = JSON.parse(data);
        this.processWebSocketMessage(parsedData);
      } catch (e) {
        console.error("解析全局 WebSocket 消息失败:", e, data);
      }
    },

    handleGlobalWsOpen() {
      console.log("全局 WebSocket 已连接");

      // 发送上线通知
      const onlineMsg = {
        type: 'system',
        content: `${this.user.userName} 进入了书信阁`,
        timestamp: Date.now(),
        id: 'login_' + Date.now()
      };
      this.addSystemMessage(onlineMsg);

      // 可以在这里请求在线用户列表等初始化数据
     // this.requestInitialData();
    },

    handleGlobalWsClose() {
      console.log("全局 WebSocket 已关闭");
      this.$Message.warning('连接已断开');
    },

    handleGlobalWsError(error) {
      console.error("全局 WebSocket 错误:", error);
      this.$Message.error('连接发生错误');
    },

    // ========== 消息处理（原有的 getMessageCallback 逻辑） ==========
    processWebSocketMessage(data) {
      if (data === "连接成功" || data === "HeartBeat") {
        return;
      }
      if (data.users) {
        // 在线用户列表更新
         // 静默模式下不触发系统消息提醒
         if (!data.users.silent) {
            console.log('在线用户更新（非静默）:', data.users.length, '人');
          } else {
            console.log('在线用户更新（静默）:', data.users.length, '人');
          }
        //this.updateOnlineUsers(data.users);
      } else if (data.type === 'system') {
        // 系统消息
        this.handleSystemMessage(data);
      } else if (data.type === 'world') {
        // 世界消息
        this.handleWorldMessage(data);
      } else if (data.type === 'error') {
        // 错误消息
        this.handleErrorMessage(data);
      } else {
        // 普通聊天消息
        this.handleChatMessage(data);
      }
    },
    // 处理小窗口发送的消息
    handleSendChatMessage(message) {
      // 确保消息包含必要的字段
      const fullMessage = {
        ...message,
        from: this.user.account,
        fromNickname: this.user.userName,
        fromFace: this.user.face,
        fromLevel: this.user.level,
        fromRealm: this.user.realmName,
        timestamp: message.timestamp || Date.now()
      };

      // 使用全局 WebSocket 发送
      if (window.chatWebSocket && window.chatWebSocket.readyState === WebSocket.OPEN) {
        try {
          window.chatWebSocket.send(JSON.stringify(fullMessage));
          console.log("通过全局 WebSocket 发送消息:", fullMessage);

          // 同时添加到本地消息列表（如果需要）
          this.handleChatMessage(fullMessage);

        } catch (error) {
          console.error('发送消息失败:', error);
          this.$Message.error('发送失败，请检查网络连接');
        }
      } else {
        console.error('WebSocket 连接未就绪');
        this.$Message.error('连接未就绪，无法发送消息');
      }
    },
    // ========== 初始化方法 ==========
    initUserData() {
      // 从sessionStorage获取用户信息
      const userData = JSON.parse(window.localStorage.getItem("userActivity")) || {};
      this.user = {
        account: userData.account || 'guest_' + Date.now(),
        userName: userData.userName || '游客',
        face: userData.face || '',
        level: userData.level || 1,
        realmName: userData.realmName || '凡人'
      };

      console.log("当前用户信息:", this.user);
    },

    // ========== WebSocket相关方法 ==========
    /*createWebSocket() {
      const webSocketLink = `ws://1.15.75.55/foodie-api/imserver/${this.user.account}`;

      try {
        if ("WebSocket" in window) {
          this.socketObj = new WebSocket(webSocketLink);
        } else if ("MozWebSocket" in window) {
          this.socketObj = new MozWebSocket(webSocketLink);
        }
        this.socketEventBind();
      } catch (e) {
        console.error("WebSocket创建失败:", e);
        this.socketReconnect();
      }
    },*/

    /*socketEventBind() {
      this.socketObj.onopen = this.onopenCallback;
      this.socketObj.onerror = this.onerrorCallback;
      this.socketObj.onclose = this.oncloseCallback;
      this.socketObj.onmessage = this.getMessageCallback;

      window.onbeforeunload = () => {
        if (this.socketObj) {
          this.socketObj.close();
        }
      };
    },
*/
   /* socketReconnect() {
      if (this.socketReconnectLock) return;
      this.socketReconnectLock = true;

      this.socketReconnectTimer && clearTimeout(this.socketReconnectTimer);
      this.socketReconnectTimer = setTimeout(() => {
        console.log("WebSocket:重连中...");
        this.socketReconnectLock = false;
       // this.createWebSocket();
      }, 4000);
    },*/

   /* onopenCallback(event) {
      console.log("WebSocket:已连接");
      this.heartCheck.reset().start();

      // 发送上线通知（本地）
      this.addSystemMessage({
        type: 'system',
        content: `${this.user.userName} 进入了书信阁`,
        timestamp: Date.now(),
        id: 'login_' + Date.now()
      });
    },*/

   /* onerrorCallback(event) {
      console.error("WebSocket:发生错误", event);
      this.$Message.error('连接发生错误，正在尝试重连...');
      this.socketReconnect();
    },*/

   /* oncloseCallback(event) {
      console.log("WebSocket:已关闭");
      this.heartCheck.reset();
      if (!this.socketLeaveFlag) {
        this.$Message.warning('连接已断开，正在重新连接...');
        this.socketReconnect();
      }
    },*/
    /*getMessageCallback(msg) {
      if (msg.data.indexOf("HeartBeat") > -1) {
        this.heartCheck.reset().start();
        return;
      }

      if (msg.data === "连接成功") {
        console.log("WebSocket连接成功");
        return;
      }

      try {
        const data = JSON.parse(msg.data);
        // 如果是村口频道的消息，转发到小窗口
        if (data.pindao === '桑林村村口') {
          this.$bus.$emit('VILLAGE_MESSAGE', data);
        }

        if (data.type === 'userlist') {
          this.updateOnlineUsers(data.users);
        } else if (data.type === 'system') {
          this.handleSystemMessage(data);
        } else if (data.type === 'world') {
          this.handleWorldMessage(data);
        } else {
          this.handleChatMessage(data);
        }
      } catch (e) {
        console.error("消息解析失败:", e, msg.data);
      }
    },*/

    updateOnlineUsers(serverUsers) {
      const currentAccount = this.user.account;
      const oldUsers = [...this.users];

      // 更新用户列表，保留未读计数
      this.users = serverUsers.filter(user => user.account !== currentAccount)
        .map(serverUser => {
          const oldUser = oldUsers.find(u => u.account === serverUser.account);
          return {
            account: serverUser.account,
            userName: serverUser.userName || serverUser.account,
            face: serverUser.face || '',
            level: serverUser.level || 1,
            realmName: serverUser.realmName || '凡人',
            unread: oldUser ? oldUser.unread : 0,
            messages: oldUser ? oldUser.messages : []
          };
        });
    },
    // 更新未读消息总数
    updateUnreadCount() {
      // 触发全局未读消息更新事件
      this.$bus.$emit('GLOBAL_UNREAD_UPDATE', this.totalUnread);
    },
    // ========== 消息处理方法 ==========
    handleSystemMessage(data) {
      const systemChannel = this.chatChannels.find(c => c.id === 'system');
      if (systemChannel) {
        systemChannel.messages.unshift(data);
        this.systemMessages.unshift(data);
        if (systemChannel.messages.length > this.maxChannelMessages) {
          systemChannel.messages = systemChannel.messages.slice(0, this.maxChannelMessages);
        }
        if (this.systemMessages.length > this.maxChannelMessages) {
          this.systemMessages = this.systemMessages.slice(0, this.maxChannelMessages);
        }

        if (this.activeChannel.id !== 'system') {
          systemChannel.unread = (systemChannel.unread || 0) + 1;
          //this.updateUnreadCount(); // 添加这一行
        }
      }
    },

    handleWorldMessage(data) {
      const worldChannel = this.chatChannels.find(c => c.id === 'world');
      if (worldChannel) {
        worldChannel.messages.unshift(data);
        this.worldMessages.unshift(data);
        if (worldChannel.messages.length > this.maxChannelMessages) {
          worldChannel.messages = worldChannel.messages.slice(0, this.maxChannelMessages);
        }
        if (this.worldMessages.length > this.maxChannelMessages) {
          this.worldMessages = this.worldMessages.slice(0, this.maxChannelMessages);
        }

        if (this.activeChannel.id !== 'world') {
          worldChannel.unread = (worldChannel.unread || 0) + 1;
          this.updateUnreadCount(); // 添加这一行
        }
      }
    },

    handleChatMessage(data) {
      const message = {
        ...data,
        timestamp: data.timestamp || Date.now(),
        id: Date.now() + '_' + Math.random().toString(36).substr(2, 9)
      };

      if (data.pindao === '世界频道' || data.type === 'world') {
        this.handleWorldMessage(message);
      } else if (data.pindao === '桑林村村口') {
        this.handleVillageMessage(message);
      } else if (data.to) {
        this.handlePrivateMessage(message);
      }

      this.triggerAutoBattle(data);
    },

    handleVillageMessage(data) {
      const villageChannel = this.chatChannels.find(c => c.id === 'village');
      if (villageChannel) {
        villageChannel.messages.unshift(data);
        if (villageChannel.messages.length > this.maxChannelMessages) {
          villageChannel.messages = villageChannel.messages.slice(0, this.maxChannelMessages);
        }

        if (this.activeChannel.id !== 'village') {
          villageChannel.unread = (villageChannel.unread || 0) + 1;
        }
      }
    },

    handlePrivateMessage(data) {
      const otherAccount = data.from === this.user.account ? data.to : data.from;
      const user = this.users.find(u => u.account === otherAccount);

      if (user) {
        if (!user.messages) user.messages = [];
        user.messages.unshift(data);

        if (user.messages.length > 200) {
          user.messages = user.messages.slice(0, 200);
        }

        if (this.activeChannel.id !== user.account) {
          user.unread = (user.unread || 0) + 1;
        }
      }
    },

    addSystemMessage(message) {
      const systemChannel = this.chatChannels.find(c => c.id === 'system');
      if (systemChannel) {
        systemChannel.messages.unshift(message);
        this.systemMessages.unshift(message);
        if (systemChannel.messages.length > this.maxChannelMessages) {
          systemChannel.messages = systemChannel.messages.slice(0, this.maxChannelMessages);
        }
        if (this.systemMessages.length > this.maxChannelMessages) {
          this.systemMessages = this.systemMessages.slice(0, this.maxChannelMessages);
        }
      }
    },

    getPrivateMessages(account) {
      const user = this.users.find(u => u.account === account);
      return user ? user.messages || [] : [];
    },

    // ========== 用户信息相关方法 ==========
    getUserFace(faceUrl) {
      if (!faceUrl) {
        return 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png';
      }
      return faceUrl.startsWith('http') ? faceUrl : `https://${faceUrl}`;
    },

    getUserAvatar(account) {
      if (account === this.user.account) {
        return this.getUserFace(this.user.face);
      }

      const user = this.users.find(u => u.account === account);
      if (user) {
        return this.getUserFace(user.face);
      }

      return 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png';
    },

    getDisplayName(account) {
      if (account === this.user.account) {
        return this.user.userName;
      }

      const user = this.users.find(u => u.account === account);
      return user ? user.userName : account;
    },

    getUserLevel(account) {
      if (account === this.user.account) {
        return this.user.level;
      }

      const user = this.users.find(u => u.account === account);
      return user ? user.level : 1;
    },

    getUserRealm(account) {
      if (account === this.user.account) {
        return this.user.realmName;
      }

      const user = this.users.find(u => u.account === account);
      return user ? user.realmName : '凡人';
    },

    // ========== 界面交互方法 ==========
    switchChannel(channel) {
      this.activeChannel = channel;
      this.clearUnread(channel.id);
      this.scrollToBottom();
    },

    switchToPrivateChat(user) {
      this.activeChannel = {
        id: user.account,
        name: user.userName,
        type: 'private'
      };
      this.clearUnread(user.account);
      this.scrollToBottom();
    },

    // ========== 发送消息（修改为使用全局 WebSocket） ==========
    send() {
      if (!this.text.trim()) {
        this.$Message.warning('请输入消息内容');
        return;
      }

      // 检查全局 WebSocket 连接状态
      if (!window.chatWebSocket || window.chatWebSocket.readyState !== WebSocket.OPEN) {
        this.$Message.error('连接未就绪，请稍后再试');
        return;
      }

      const message = {
        from: this.user.account,
        fromNickname: this.user.userName,
        fromFace: this.user.face,
        fromLevel: this.user.level,
        fromRealm: this.user.realmName,
        text: this.text,
        timestamp: Date.now()
      };

      // 根据发送类型设置消息属性
      if (this.sendType === 'world') {
        message.type = 'world';
        message.pindao = '世界频道';
      } else if (this.activeChannel.type === 'private') {
        message.to = this.activeChannel.id;
      } else {
        message.pindao = this.activeChannel.name;
      }

      try {
        // 通过全局 WebSocket 发送消息
        window.chatWebSocket.send(JSON.stringify(message));
        console.log("通过全局 WebSocket 发送消息:", message);

        // 本地显示
        const displayMessage = {
          ...message,
          from: this.user.account,
          fromNickname: this.user.userName
        };

        if (message.type === 'world') {
          this.handleWorldMessage(displayMessage);
        } else if (this.activeChannel.type === 'private') {
          this.handlePrivateMessage(displayMessage);
        } else {
          this.handleVillageMessage(displayMessage);
        }

        this.text = '';
        this.scrollToBottom();

      } catch (error) {
        console.error("发送消息失败:", error);
        this.$Message.error('发送消息失败');
      }
    },

    clearChat() {
      this.$Modal.confirm({
        title: '清空聊天记录',
        content: '确定要清空当前聊天记录吗？',
        onOk: () => {
          if (this.activeChannel.type === 'private') {
            const user = this.users.find(u => u.account === this.activeChannel.id);
            if (user) {
              user.messages = [];
              this.$Message.success('已清空私聊记录');
            }
          } else {
            const channel = this.chatChannels.find(c => c.id === this.activeChannel.id);
            if (channel) {
              channel.messages = [];
              this.$Message.success('已清空聊天记录');
            }
          }
        }
      });
    },

    // ========== 未读消息管理 ==========
    getUnreadCount(channelId) {
      if (channelId === 'system' || channelId === 'world' || channelId === 'village') {
        const channel = this.chatChannels.find(c => c.id === channelId);
        return channel ? channel.unread : 0;
      } else {
        const user = this.users.find(u => u.account === channelId);
        return user ? user.unread : 0;
      }
    },

    clearUnread(channelId) {
      if (channelId === 'system' || channelId === 'world' || channelId === 'village') {
        const channel = this.chatChannels.find(c => c.id === channelId);
        if (channel) channel.unread = 0;
      } else {
        const user = this.users.find(u => u.account === channelId);
        if (user) user.unread = 0;
      }
    },

    clearAllUnread() {
      this.chatChannels.forEach(channel => {
        channel.unread = 0;
      });
      this.users.forEach(user => {
        user.unread = 0;
      });
      this.$Message.success('全部标记为已读');
    },

    showMessageCenter() {
      this.messageModalVisible = true;
      this.activeTab = 'system';
    },

    // ========== 工具方法 ==========
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messageContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },

    formatTime(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      const now = new Date();

      if (now.toDateString() === date.toDateString()) {
        return date.getHours().toString().padStart(2, '0') + ':' +
          date.getMinutes().toString().padStart(2, '0');
      } else {
        return (date.getMonth() + 1) + '-' + date.getDate();
      }
    },

    formatFullTime(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')} ` +
        `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    },

    triggerAutoBattle(data) {
      const battleKeywords = ['战斗', '攻击', 'battle', '修士', 'fight', '出发', '开始', '冲', '干', '打怪'];

      if (data.text && battleKeywords.some(keyword =>
        data.text.toLowerCase().includes(keyword.toLowerCase())
      )) {
        this.$bus.$emit('TRIGGER_AUTO_BATTLE', {
          triggerSource: 'websocket_message',
          message: data,
          timestamp: Date.now(),
          fromUser: data.from
        });

        console.log('WebSocket消息触发自动战斗:', data.text);
      }
    }
  }
}
</script>

<style scoped>
/* 主容器 */
.chat-container {
  padding: 15px;
  height: calc(100vh - 100px);
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.chat-row {
  height: 100%;
}

/* 左侧面板 */
.left-panel {
  height: 100%;
  padding-right: 15px;
}

.online-card {
  height: 100%;
  border-radius: 10px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
  border: 1px solid #e8eaec;
  overflow: hidden;
}

.online-title {
  color: #6e44c0;
  font-size: 16px;
  font-weight: 600;
  padding: 15px 20px;
  border-bottom: 1px solid #f0f0f0;
  background: linear-gradient(to right, #f9f0ff, #ffffff);
  display: flex;
  align-items: center;
}

.online-title i {
  margin-right: 8px;
}

.online-count {
  margin-left: 8px;
  padding: 2px 8px;
  background: #6e44c0;
  color: white;
  border-radius: 10px;
  font-size: 12px;
  font-weight: normal;
}

.channel-list {
  height: calc(100% - 60px);
  overflow-y: auto;
  padding: 10px 0;
}

.channel-item {
  padding: 12px 20px;
  margin: 4px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid transparent;
}

.channel-item:hover {
  background-color: #f5f5f5;
  border-color: #e8eaec;
}

.channel-item.active {
  background: linear-gradient(to right, #f0e6ff, #ffffff);
  border-color: #6e44c0;
  border-left: 4px solid #6e44c0;
}

.channel-info {
  display: flex;
  align-items: center;
}

.channel-info i {
  margin-right: 10px;
  color: #6e44c0;
}

.channel-name {
  font-size: 14px;
  font-weight: 500;
}

.channel-badge {
  min-width: 18px;
  height: 18px;
  line-height: 18px;
  padding: 0 6px;
  border-radius: 9px;
}

.channel-divider {
  padding: 12px 20px;
  color: #666;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  border-top: 1px solid #eee;
  margin: 15px 10px 10px 10px;
  background: #fafafa;
  border-radius: 6px;
  display: flex;
  align-items: center;
}

.channel-divider i {
  margin-right: 8px;
}

.user-item {
  padding: 12px 20px;
  margin: 4px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  position: relative;
  border: 1px solid transparent;
}

.user-item:hover {
  background-color: #f5f5f5;
  border-color: #e8eaec;
}

.user-item.active {
  background: linear-gradient(to right, #e6f7ff, #ffffff);
  border-color: #2d8cf0;
  border-left: 4px solid #2d8cf0;
}

.user-avatar {
  margin-right: 12px;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.user-info {
  flex: 1;
  min-width: 0;
}

.user-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-details {
  display: flex;
  gap: 4px;
}

.user-details >>> .ivu-tag {
  margin-right: 0;
  height: 20px;
  line-height: 18px;
}

.user-badge {
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
}

.empty-users {
  text-align: center;
  padding: 20px;
  color: #999;
  font-size: 12px;
}

/* 右侧主聊天区 */
.right-panel {
  height: 100%;
}

.chat-main {
  height: 100%;
  background: white;
  border-radius: 10px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
  border: 1px solid #e8eaec;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-header {
  padding: 0 24px;
  height: 60px;
  border-bottom: 1px solid #e8eaec;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(to right, #f9f0ff, #ffffff);
}

.chat-title {
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.chat-title i {
  margin-right: 12px;
  color: #6e44c0;
}

.title-text {
  color: #6e44c0;
}

.channel-name {
  margin-left: 8px;
  color: #666;
  font-weight: normal;
}

.header-actions {
  display: flex;
  align-items: center;
}

.total-badge {
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.3s ease;
  background: #fff;
  border: 1px solid #e8eaec;
}

.total-badge:hover {
  background: #f5f5f5;
  transform: scale(1.05);
}

.total-badge >>> .ivu-badge-count {
  box-shadow: 0 0 0 1px #fff;
}

/* 消息容器 */
.message-container {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  background: linear-gradient(180deg, #fafafa 0%, #ffffff 100%);
  position: relative;
}

.welcome-message {
  text-align: center;
  padding: 30px 0;
  margin-bottom: 20px;
  border-bottom: 1px dashed #e8eaec;
}

.welcome-content {
  display: inline-block;
  text-align: center;
}

.welcome-content h3 {
  margin: 12px 0 8px 0;
  color: #6e44c0;
  font-size: 18px;
}

.welcome-content p {
  color: #666;
  font-size: 14px;
  margin: 0;
}

.system-message {
  text-align: center;
  margin: 10px 0;
}

.system-content {
  display: inline-block;
  background: #f0f0f0;
  color: #666;
  font-size: 12px;
  padding: 6px 16px;
  border-radius: 15px;
  border: 1px dashed #ddd;
}

.system-content i {
  margin-right: 6px;
  vertical-align: middle;
}

.system-time {
  margin-left: 8px;
  color: #999;
  font-size: 11px;
}

.message-wrapper {
  margin-bottom: 20px;
}

/* 世界消息 */
.world-message {
  display: flex;
  align-items: flex-start;
  background: linear-gradient(135deg, #fff8e1 0%, #fff3cd 100%);
  padding: 16px;
  border-radius: 10px;
  border-left: 4px solid #ff9800;
  margin: 16px 0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.world-avatar {
  margin-right: 12px;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.world-content {
  flex: 1;
  min-width: 0;
}

.world-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.world-tag {
  color: #ff9800;
  font-weight: bold;
  margin-right: 8px;
  font-size: 13px;
}

.world-sender {
  color: #333;
  font-size: 14px;
  margin-right: 12px;
}

.world-user-info {
  display: flex;
  gap: 4px;
}

.world-user-info >>> .ivu-tag {
  margin-right: 0;
  height: 20px;
  line-height: 18px;
}

.world-text {
  color: #333;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 6px;
}

/* 普通消息 */
.other-message, .self-message {
  display: flex;
  margin-bottom: 20px;
}

.other-message {
  justify-content: flex-start;
}

.self-message {
  justify-content: flex-end;
}

.message-avatar-container {
  position: relative;
  margin: 0 12px;
}

.message-avatar {
  border: 2px solid #fff;
  box-shadow: 0 3px 6px rgba(0,0,0,0.1);
}

.avatar-badge {
  position: absolute;
  bottom: -4px;
  right: -4px;
  background: #2d8cf0;
  color: white;
  font-size: 10px;
  padding: 1px 4px;
  border-radius: 8px;
  border: 2px solid #fff;
  min-width: 24px;
  text-align: center;
}

.self-badge {
  background: #6e44c0;
}

.message-content {
  max-width: 70%;
  min-width: 200px;
}

.message-info {
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.self-info {
  justify-content: flex-end;
}

.sender-name {
  font-size: 13px;
  color: #333;
  font-weight: 500;
  margin: 0 8px;
}

.message-info >>> .ivu-tag {
  margin-right: 6px;
}

.message-time {
  font-size: 12px;
  margin-right: 10px;
  color: #999;
}

/* 消息气泡 */
.bubble {
  padding: 12px 16px;
  border-radius: 18px;
  word-wrap: break-word;
  line-height: 1.5;
  font-size: 14px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
}

.other-bubble {
  background: #ffffff;
  border: 1px solid #e8eaec;
  border-top-left-radius: 4px;
  color: #333;
}

.self-bubble {
  background: linear-gradient(135deg, #6e44c0, #8a67c9);
  color: white;
  border-top-right-radius: 4px;
}

/* 输入区域 */
.input-area {
  border-top: 1px solid #e8eaec;
  background: white;
}

.input-toolbar {
  padding: 12px 24px;
  border-bottom: 1px solid #f5f5f5;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fafafa;
}

.toolbar-left {
  display: flex;
  align-items: center;
}

.toolbar-left >>> .ivu-radio-wrapper {
  margin-right: 16px;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.toolbar-left >>> .ivu-radio-wrapper:hover {
  background: #f5f5f5;
}

.toolbar-left >>> .ivu-radio-wrapper i {
  margin-right: 4px;
  vertical-align: middle;
}

.toolbar-right .user-info-tag {
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, #f0e6ff, #ffffff);
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  color: #6e44c0;
  border: 1px solid #e8eaec;
}

.textarea-container {
  padding: 20px 24px;
  position: relative;
}

.chat-textarea {
  width: 100%;
  border: none;
  outline: none;
  resize: none;
  font-size: 14px;
  line-height: 1.5;
  font-family: 'Helvetica Neue', Helvetica, 'PingFang SC', sans-serif;
  padding: 12px;
  border: 1px solid #dcdee2;
  border-radius: 8px;
  transition: all 0.3s ease;
  background: #fafafa;
}

.chat-textarea:focus {
  border-color: #6e44c0;
  box-shadow: 0 0 0 2px rgba(110, 68, 192, 0.2);
  background: white;
}

.chat-textarea::placeholder {
  color: #c5c8ce;
}

.textarea-tips {
  position: absolute;
  right: 24px;
  bottom: 0;
  font-size: 11px;
  color: #999;
  padding: 4px 8px;
  background: rgba(255,255,255,0.9);
  border-radius: 4px 4px 0 0;
  border: 1px solid #f0f0f0;
  border-bottom: none;
}

.textarea-tips i {
  margin-right: 4px;
  vertical-align: middle;
}

.input-actions {
  padding: 12px 24px;
  border-top: 1px solid #f5f5f5;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fafafa;
}

.actions-left, .actions-right {
  display: flex;
  gap: 8px;
}

/* 消息中心模态框 */
.modal-header {
  padding: 16px 0;
  margin-bottom: 16px;
  border-bottom: 1px solid #e8eaec;
  display: flex;
  align-items: center;
  color: #333;
  font-size: 14px;
}

.message-tabs {
  min-height: 400px;
}

.message-tabs >>> .ivu-tabs-bar {
  margin-bottom: 16px;
}

.message-tabs >>> .ivu-tabs-nav {
  font-weight: 500;
}

.message-list {
  max-height: 400px;
  overflow-y: auto;
  padding: 4px;
}

.message-item {
  padding: 16px;
  border-bottom: 1px solid #f5f5f5;
  transition: all 0.3s ease;
  border-radius: 6px;
  margin-bottom: 8px;
}

.message-item:hover {
  background-color: #fafafa;
}

.message-item-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.message-item-header i {
  margin-right: 8px;
}

.message-item-sender-info {
  flex: 1;
  margin: 0 12px;
}

.message-item-sender {
  font-size: 14px;
  color: #333;
  font-weight: 500;
  margin-bottom: 4px;
  display: block;
}

.sender-details {
  display: flex;
  gap: 4px;
}

.sender-details >>> .ivu-tag {
  margin-right: 0;
  height: 20px;
  line-height: 18px;
}

.message-item-time {
  font-size: 12px;
  color: #999;
  white-space: nowrap;
}

.message-item-content {
  font-size: 14px;
  color: #333;
  line-height: 1.5;
  padding-left: 24px;
}

.self-item {
  background-color: #f9f0ff;
  border: 1px solid #f0e6ff;
}

.user-selector {
  margin-bottom: 20px;
}

.empty-message {
  text-align: center;
  padding: 60px 20px;
  color: #999;
  font-size: 14px;
}

.empty-message i {
  margin-bottom: 16px;
  opacity: 0.5;
}

.empty-message p {
  margin: 0;
}

/* 自定义滚动条 */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 响应式调整 */
@media (max-width: 1200px) {
  .left-panel {
    span: 5;
  }

  .right-panel {
    span: 19;
  }
}

@media (max-width: 992px) {
  .left-panel {
    span: 6;
  }

  .right-panel {
    span: 18;
  }

  .message-content {
    max-width: 80%;
  }
}

@media (max-width: 768px) {
  .chat-row {
    flex-direction: column;
  }

  .left-panel, .right-panel {
    span: 24;
  }

  .left-panel {
    margin-bottom: 16px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Im.vue